import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Avatar, Flex, Input, Select, useToast } from '@chakra-ui/react';
import { ProfessoresGet } from '../Professores';
import { adicionarAula, listarAlunosPorProfessorId, listarBooks } from '../../services/api';
import { CampoForm } from '../../components/CampoForm';
import { DrawerForm } from '../../components/DrawerForm';
import { ErrosForm, useErrosForm } from '../../hooks/useErrosForm';
import { vazio } from '../../utils/validacao';
import { AlunosType } from '.';
import { BookGet } from '../Books';
import useAuth from '../../hooks/useAuth';

type DrawerCreateProps = {
	isOpen: boolean;
	onOpen: () => void;
	onClose: () => void;
	afterClose: () => void;
	professor: ProfessoresGet;
};

type Campo = 'titulo' | 'data' | 'alunoId' | 'materialId';

export function DrawerCreate({ isOpen, onClose, afterClose, professor }: DrawerCreateProps) {
	const toast = useToast();
	const { user, functionId } = useAuth();
	const { erros, validar, limparErro, limparTudo } = useErrosForm<Campo>();

	const [ loading, setLoading ] = useState(false);

	const [ titulo, setTitulo ] = useState<string>('');
	const [ data, setData ] = useState<string>('');
	const [ alunos, setAlunos ] = useState<AlunosType[]>([]);
	const [ alunoId, setAlunoId ] = useState<string>('');
	const [ materialId, setMaterialId ] = useState<string>('');
	const [ books, setBooks ] = useState<BookGet[]>([]);

	const buscarAlunos = useCallback(
		() => {
			setLoading(true);
			listarAlunosPorProfessorId(functionId)
				.then((response) => {
					setLoading(false);
					setAlunos(response.data);
				})
				.catch((error) => {
					setLoading(false);
					if (!error.response) return;
					toast({
						title: `Não foi possível listar alunos.`,
						status: 'error',
						isClosable: true
					});
				});
		},
		[ functionId, toast ]
	);

	const buscarMateriais = useCallback(
		() => {
			listarBooks()
				.then((response) => {
					setBooks(response.data);
				})
				.catch((error) => {
					if (!error.response) return;
					toast({
						title: `Não foi possível listar os materiais.`,
						status: 'error',
						isClosable: true
					});
				});
		},
		[ toast ]
	);

	useEffect(
		() => {
			// So carrega quando o drawer esta aberto; sem isto o efeito refazia as
			// requisicoes tambem ao fechar (isOpen indo para false).
			if (!isOpen) return;
			buscarAlunos();
			buscarMateriais();
		},
		[ isOpen, professor, buscarAlunos, buscarMateriais ]
	);

	const montarErros = (): ErrosForm<Campo> => {
		const encontrados: ErrosForm<Campo> = {};

		if (vazio(titulo)) encontrados.titulo = 'Título é obrigatório!';
		if (vazio(data)) encontrados.data = 'Data é obrigatória!';
		if (!alunoId) encontrados.alunoId = 'Selecione o aluno!';
		if (!materialId) encontrados.materialId = 'Selecione o material!';

		return encontrados;
	};

	const handleSalvar = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!validar(montarErros())) return;

		setLoading(true);
		adicionarAula({
			data,
			alunoId,
			professorId: functionId,
			status: 'AGENDADA',
			titulo: titulo.trim(),
			materialId
		})
			.then(() => {
				setLoading(false);
				toast({
					title: `Aula adicionada com sucesso.`,
					status: 'success',
					isClosable: true
				});
				resetForm();
				onClose();
				afterClose();
			})
			.catch((error) => {
				setLoading(false);
				if (!error.response) return;
				const message = error.response.data?.message;
				toast({
					title: typeof message === 'string' ? message : `Não foi possível adicionar nova aula.`,
					status: 'error',
					isClosable: true
				});
			});
	};

	const resetForm = () => {
		setTitulo('');
		setData('');
		setAlunoId('');
		setMaterialId('');
		limparTudo();
	};

	const handleFechar = () => {
		resetForm();
		onClose();
		afterClose();
	};

	return (
		<DrawerForm
			isOpen={isOpen}
			onClose={handleFechar}
			titulo="Nova Aula"
			formId="form-nova-aula-professor"
			loading={loading}
			onSubmit={handleSalvar}
		>
			<CampoForm label="Professor" isRequired={false}>
				<Flex
					gap={'10px'}
					alignItems="center"
					justifyContent="center"
					paddingTop="10px"
					paddingBottom="10px"
					borderWidth="1px"
					borderColor="gray.200"
					borderRadius="10px"
				>
					<Avatar size="sm" name={user?.name} src={user?.avatar} />
					{`${user?.name}`}
				</Flex>
			</CampoForm>

			<CampoForm label="Título" htmlFor="titulo" erro={erros.titulo}>
				<Input
					id="titulo"
					placeholder="Insira o Título"
					onChange={(e) => {
						setTitulo(e.target.value);
						limparErro('titulo');
					}}
					value={titulo}
				/>
			</CampoForm>

			<CampoForm label="Data" htmlFor="data" erro={erros.data}>
				<Input
					id="data"
					type="datetime-local"
					onChange={(e) => {
						setData(e.target.value);
						limparErro('data');
					}}
					value={data}
				/>
			</CampoForm>

			<CampoForm label="Aluno" htmlFor="aluno" erro={erros.alunoId}>
				<Select
					id="aluno"
					placeholder="Selecione o Aluno"
					onChange={(e) => {
						setAlunoId(e.target.value);
						limparErro('alunoId');
					}}
					value={alunoId}
				>
					{alunos.map((item) => (
						<option value={item.id} key={item.id} style={{ color: 'black' }}>
							{`${item.nome} ${item.sobrenome}`}
						</option>
					))}
				</Select>
			</CampoForm>

			<CampoForm label="Material" htmlFor="material" erro={erros.materialId}>
				<Select
					id="material"
					placeholder="Selecione o Material"
					onChange={(e) => {
						setMaterialId(e.target.value);
						limparErro('materialId');
					}}
					value={materialId}
				>
					{books.map((item) => (
						<option value={item.id} key={item.id} style={{ color: 'black' }}>
							{item.nome}
						</option>
					))}
				</Select>
			</CampoForm>
		</DrawerForm>
	);
}
